import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import type { RemoveUserResult } from './useOrgAdminUsers';
import { OrgUser } from '../types/admin';

interface OrgUsersResponse {
  success: boolean;
  data: OrgUser[];
}

interface OrgUserResponse {
  success: boolean;
  data: OrgUser;
}

interface RemoveUserResponse {
  success: boolean;
  deleted?: boolean;
}

export function useOrgUsers(orgId: string) {
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<OrgUsersResponse>(`/admin/organizations/${orgId}/users`)
      .then(res => {
        if (!cancelled) {
          setUsers(res.data);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load users');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const addUser = useCallback(
    async (email: string, role: OrgUser['role']) => {
      const res = await api.post<OrgUserResponse>(
        `/admin/organizations/${orgId}/users`,
        { email, role },
      );
      // If reactivated an existing user, update in place; otherwise append
      setUsers(prev => {
        const exists = prev.some(u => u.uid === res.data.uid);
        return exists ? prev.map(u => u.uid === res.data.uid ? res.data : u) : [...prev, res.data];
      });
      return res.data;
    },
    [orgId],
  );

  const makeMeAdmin = useCallback(async () => {
    const res = await api.post<OrgUserResponse>(
      `/admin/organizations/${orgId}/users/me`,
      {},
    );
    setUsers(prev => {
      // Replace existing entry if already present (e.g. invited state), else append.
      const exists = prev.some(u => u.uid === res.data.uid);
      return exists ? prev.map(u => u.uid === res.data.uid ? res.data : u) : [...prev, res.data];
    });
    return res.data;
  }, [orgId]);

  const updateRole = useCallback(
    async (uid: string, role: OrgUser['role']) => {
      const res = await api.patch<OrgUserResponse>(
        `/admin/organizations/${orgId}/users/${uid}`,
        { role },
      );
      setUsers(prev => prev.map(u => u.uid === uid ? res.data : u));
      return res.data;
    },
    [orgId],
  );

  /**
   * Remove a member. The backend deletes members who never activated (pending
   * invites) and deactivates everyone else; `deleted` says which happened.
   */
  const removeUser = useCallback(
    async (uid: string): Promise<RemoveUserResult> => {
      const res = await api.del<RemoveUserResponse>(`/admin/organizations/${orgId}/users/${uid}`);
      const deleted = res.deleted === true;
      setUsers(prev => deleted
        ? prev.filter(u => u.uid !== uid)
        : prev.map(u => u.uid === uid ? { ...u, status: 'inactive' as const } : u));
      return { deleted };
    },
    [orgId],
  );

  const activateUser = useCallback(
    async (uid: string) => {
      const res = await api.post<OrgUserResponse>(
        `/admin/organizations/${orgId}/users/${uid}/activate`,
        {},
      );
      setUsers(prev => prev.map(u => u.uid === uid ? res.data : u));
      return res.data;
    },
    [orgId],
  );

  const resendInvite = useCallback(
    async (uid: string) => {
      await api.post(`/admin/organizations/${orgId}/users/${uid}/resend-invite`, {});
    },
    [orgId],
  );

  return { users, loading, error, addUser, makeMeAdmin, updateRole, removeUser, activateUser, resendInvite };
}
